import {
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Actions from "./components/Actions/Actions";
import LeadHeader from "./components/LeadHeader";
import StatusAndUsersSection from "./components/StatusAndUsersSection";
import StageSection from "./components/StageSection";
import { LeadFormProvider, useLeadForm } from "../../contexts/FormLeadContext";
import { ActiveEntityRequiredFieldMarks } from "../../requirements/requiredFieldMarks.ui";
import DraggableTabsWrapper from "@/components/ui/tabs/DraggableTabsWrapper";
import { setLeadCurrentTab } from "../../stores/leadFunctionalSlice";
import { setDrawerTabOrder } from "@/features/settings/stores/managePreferencesSlice";
import { useAppDispatch, useAppSelector } from "@/stores/hook";

import ViewAndEditRightSideSheet from "@/components/ui/others/ViewAndEditRightSideSheet";
import { useBreakpoint } from "@/hooks/useViewport";

import FollowUpOverrideConfirmation from "./Modals/FollowUpOverrideConfirmation";

import useCurrentLead from "../../api/useCurrentLead";
import FullScreenLoader from "@/components/feedback/full-screen-loader/FullScreenLoader";
import Overview from "./components/Overview/Overview";
import Attachment from "./components/Attachment/Attachment";
import FollowUp from "./components/Followup/FollowUp";
import Deadline from "./components/Deadline/Deadline";
import Chat from "./components/Chat/Chat";
import Tracks from "./components/Tracks/Tracks";
import ActivityLog from "./components/Activity/ActivityLog";
import CallHistory from "./components/CallHistory/CallHIstory";
import DynamicStageDrawer from "../../../../module/DynamicNichDrawer";
import Portal from "./components/Portal/Portal";
import Ongoing from "./components/Ongoing/Ongoing";
import { getAllDrawers } from "@/config/drawerConfigs/drawerRouteAdapters";
import { useActiveConnectedTools } from "@/features/connectedTools/api/useConnectedTools";
import LeadToolsTab from "@/features/connectedTools/components/lead/LeadToolsTab";
import LeadToolStrip from "@/features/connectedTools/components/lead/LeadToolStrip";
import { toolLeadTabs } from "@/features/connectedTools/utils/toolLeadTabs";
import useStages from "@/features/stage/api/useStages";
import usePipeline from "@/features/stage/api/usePipeline";
import useBranches from "@/features/users/api/useBranches";
import AssignUserSheet from "./components/AssignUserSheet";
import PeerTransferSheet from "./components/PeerTransferSheet";
import { setIdentifierAndIdentifier } from "../../stores/childSlice";

import useIndexPageResolver from "@/module/hook/useIndexPageResolver";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { usePermissions } from "@/rbac/usePermissions";
import { selectIsTenantAdmin } from "@/rbac/rbacSlice";
import useTenantFeatures from "@/hooks/api/useTenantFeatures";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import RevokedAccessExplainer from "./components/RevokedAccessExplainer";
import { useLayoutLocked } from "@/features/leads/hooks/useLayoutLocked";
import { useLeadFormLockedFields } from "@/features/leads/api/useLeadFormConfig";

interface ViewLeadProps {
  leadId: string;
  open: boolean;
  onClose: () => void;
  /** When opened from a dynamic STAGE route, the route's stageId — used to
   *  default-select the deal in that stage. Undefined for the main Leads
   *  drawer (keeps the furthest-deal default). */
  routeStageId?: number | null;
  /** With `routeStageId`: the status / sub-status the list was filtered by,
   *  so the drawer opens on the deal that carries ALL the chips — the main
   *  Leads list filtered "Stage: Visa · Status: Visa Rejected" opens the
   *  Visa-rejected deal, not the lead's furthest live one (owner,
   *  2026-10-22: "none of it is in visa rejected"). Ignored without a stage. */
  routeStatusId?: number | null;
  routeSubStatusId?: number | null;
  /** Default-select THIS deal on open (wins over routeStageId). The
   *  dashboard follow-up rail passes the deal whose slot put the lead in
   *  the viewer's bucket, so the drawer opens on the deal they're meant to
   *  follow up — never a "random" one (user directive 2026-08-31). */
  defaultChildId?: number | null;
}

/**
 * View Lead drawer is a touch wider than the shared sheet defaults
 * (40vw desktop) — it packs Status/Users/Stage + the tabbed detail panes,
 * which felt cramped. Mobile stays full-screen. Scoped to this drawer only;
 * other consumers of ViewAndEditRightSideSheet keep the shared widths.
 */
const VIEW_LEAD_WIDTH: Record<
  "mobile" | "tablet" | "tablet-lg" | "desktop",
  string
> = {
  mobile: "100vw",
  tablet: "76vw",
  "tablet-lg": "62vw",
  desktop: "46vw",
};

function ViewLeadModalShell({
  leadId,
  onClose,
  open,
}: {
  leadId: string;
  onClose: () => void;
  open: boolean;
}) {
  const breakpoint = useBreakpoint();
  const { isLoading, lead } = useCurrentLead(leadId);
  const { currentTab } = useAppSelector((state) => state.leadFunctional);
  const { drawerTabOrder } = useAppSelector((state) => state.managePreferences);
  // Branch-based pipelines: the drawer's stage bar + tabs must reflect the
  // pipeline of the LEAD's branch. Non-forked tenants keep the exact legacy
  // `useStages()` list (the fork query stays disabled → no extra request → byte-
  // identical); a lead on a forked branch renders that fork's stages instead.
  const { hasForkedBranch } = useBranches();
  const stageScoped = hasForkedBranch && (lead as any)?.branchId != null;
  const { stages: defaultStages } = useStages();
  const { pipeline: forkStages } = usePipeline(
    undefined,
    stageScoped ? (lead as any).branchId : undefined,
    stageScoped,
  );
  const stages = stageScoped ? forkStages : defaultStages;
  const { industry } = useCurrentIndustry();
  const overrideComp = useIndexPageResolver();
  const dispatch = useAppDispatch();
  const { isViewLocked, lead: formLead } = useLeadForm();
  const isAdmin = useAppSelector(selectIsTenantAdmin);
  /**
   * Non-admin + no access → show a clean "why you can't see this" panel
   * instead of a grayed-out shell of empty Status / Sub-status / Assigned
   * / Last-Stage / Actions controls that don't do anything. Admins keep
   * the full drawer (they have access; the revoked-tab admin banner
   * tells them who lost access).
   */
  const showAccessDeniedOnly = isViewLocked && !isAdmin;
  const { canGlobal, canDeadline, canActivity } = usePermissions();
  /**
   * Deadlines tab visibility is per-stage now (`lead_deadline.level` ≥ view).
   * Bound to the lead's current stage; falls through permissively while the
   * lead payload is still loading so the tab doesn't strobe in/out.
   */
  const leadStageIdNum = useMemo(() => {
    const raw = (lead as any)?.stage?.id ?? (lead as any)?.stageId;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [lead]);
  const canViewDeadline = canDeadline(leadStageIdNum, "view");
  // Call History follows the role-wide gate only. Activity Log also needs the
  // lead's current stage's "Activity timeline" not set to Deny (the server
  // serves no timeline there); Deny never hides the calls (owner, 2026-10-28:
  // "we denied only activity log... not call logs").
  const canViewCallHistory = canGlobal("lead_management", "view_activity");
  const canViewLeadActivity = canViewCallHistory && canActivity(leadStageIdNum);
  const { hasFeature } = useTenantFeatures();
  const hasStudentPortal = hasFeature("student_portal");
  const { hiddenLeadViewTabs, dealsDisabled } = useLeadFormLockedFields();
  // Each connected tool chooses its own tab, the shared Tools tab, or neither.
  const { data: activeTools } = useActiveConnectedTools();
  const connectedToolTabs = useMemo(() => toolLeadTabs(activeTools?.tools), [activeTools]);

  const rawTabs = useMemo(() => {
    if (!lead) return [];
    const drawers = getAllDrawers(industry?.id || 1, stages);
    let next = drawers.filter((t: { value: string }) => {
      if (t.value === "Portal") return hasStudentPortal;
      if (t.value === "Fees") return false;
      // Settings → Manage → "Disable deals": the deal tabs go with the concept.
      if (dealsDisabled && DEAL_TABS.has(t.value)) return false;
      return true;
    });
    next = [...next, ...connectedToolTabs];
    if (!canViewLeadActivity) {
      next = next.filter((t: { value: string }) => t.value !== "Activity Log");
    }
    if (!canViewCallHistory) {
      next = next.filter((t: { value: string }) => t.value !== "Call History");
    }
    if (!canViewDeadline) {
      next = next.filter((t: { value: string }) => t.value !== "Deadlines");
    }
    // Settings → Manage "Lead View Tabs": tabs the workspace switched off.
    // Never leaves the drawer empty — if every remaining tab is hidden, the
    // setting is ignored rather than showing a strip with nothing in it.
    if (hiddenLeadViewTabs.length > 0) {
      const hidden = new Set(hiddenLeadViewTabs);
      const visible = next.filter(
        (t: { value: string }) => !hidden.has(t.value)
      );
      if (visible.length > 0) next = visible;
    }
    return next;
  }, [
    lead,
    industry?.id,
    stages,
    canViewLeadActivity,
    canViewCallHistory,
    canViewDeadline,
    hasStudentPortal,
    hiddenLeadViewTabs,
    dealsDisabled,
    connectedToolTabs,
  ]);

  useEffect(() => {
    const lost =
      (currentTab === "Activity Log" && !canViewLeadActivity) ||
      (currentTab === "Call History" && !canViewCallHistory);
    if (lost) dispatch(setLeadCurrentTab("Overview"));
  }, [canViewLeadActivity, canViewCallHistory, currentTab, dispatch]);

  useEffect(() => {
    if (canViewDeadline) return;
    if (currentTab !== "Deadlines") return;
    dispatch(setLeadCurrentTab("Overview"));
  }, [canViewDeadline, currentTab, dispatch]);

  const tabs = useMemo(() => {
    if (!rawTabs.length || !drawerTabOrder?.length) return rawTabs;
    const orderMap = new Map(
      drawerTabOrder.map((v: string, i: number) => [v, i])
    );
    return [...rawTabs].sort((a: any, b: any) => {
      const aIdx = orderMap.get(a.value) ?? 999;
      const bIdx = orderMap.get(b.value) ?? 999;
      return aIdx - bIdx;
    });
  }, [rawTabs, drawerTabOrder]);

  const activeTabForBar =
    tabs.find((t: any) => t.value === currentTab) ?? tabs[0];

  /**
   * Tab strip overflow → "more tabs" arrow.
   *
   * The strip scrolls horizontally but nothing told the user that Chat /
   * Activity Log / Call History / Tracks sit off to the right. The arrow
   * shines while there is more strip to reveal, slides the strip a page on
   * click, and turns into a back-to-start arrow once the end is reached.
   * The strip is mounted conditionally (loader / revoked-access / override
   * branches), so the element is tracked in state via a callback ref rather
   * than read from a plain ref on mount.
   */
  const tabStripScrollRef = useRef<HTMLDivElement | null>(null);
  const [tabStripEl, setTabStripEl] = useState<HTMLDivElement | null>(null);
  const attachTabStrip = useCallback((el: HTMLDivElement | null) => {
    tabStripScrollRef.current = el;
    setTabStripEl(el);
  }, []);
  const [tabStripOverflow, setTabStripOverflow] = useState({
    left: false,
    right: false,
  });

  useLayoutEffect(() => {
    const el = tabStripEl;
    const apply = (next: { left: boolean; right: boolean }) =>
      setTabStripOverflow((prev) =>
        prev.left === next.left && prev.right === next.right ? prev : next
      );
    if (!el) {
      apply({ left: false, right: false });
      return;
    }
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      apply({ left: el.scrollLeft > 2, right: el.scrollLeft < max - 2 });
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // The tabs render inside a nested flex row — observe it too so adding /
    // removing a tab (Portal, Deadlines, stage tabs) re-measures.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [tabStripEl, tabs.length]);

  const slideTabStrip = useCallback(() => {
    const el = tabStripScrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    if (el.scrollLeft < max - 2) {
      const step = Math.max(120, Math.round(el.clientWidth * 0.8));
      el.scrollBy({ left: step, behavior: "smooth" });
    } else {
      el.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, []);

  /** When a tab is removed (e.g. Portal / Fees), fall back so content matches the visible selected tab. */
  const contentTab = useMemo(() => {
    if (!tabs.length) return currentTab;
    return tabs.some((t: any) => t.value === currentTab)
      ? currentTab
      : ((tabs[0]?.value as string) ?? "Overview");
  }, [tabs, currentTab]);

  // "Globalise configs": the tab strip's order is the admin's for everyone.
  const layoutLocked = useLayoutLocked();
  const handleReorder = useCallback(
    (newOrder: string[]) => {
      dispatch(setDrawerTabOrder(newOrder));
    },
    [dispatch]
  );

  const renderTabContent = () => {
    if (typeof contentTab === "string" && contentTab.startsWith("tool:")) {
      return <LeadToolsTab key={contentTab} toolId={Number(contentTab.slice(5))} />;
    }
    switch (contentTab) {
      case "Overview":
        return <Overview />;
      case "Followups":
        return <FollowUp />;
      case "Deadlines":
        return <Deadline />;
      case "Attachment":
        return <Attachment />;
      case "Portal":
        return <Portal />;
      case "Payments":
        return <Ongoing />;
      case "Chat":
        return <Chat />;
      case "Tracks":
        return <Tracks />;
      case "Activity Log":
        return <ActivityLog />;
      case "Call History":
        return <CallHistory />;
      case "Tools":
        return <LeadToolsTab />;
      default:
        const stageCurrentTab =
          stages?.find((val: any) => val.name === contentTab) ||
          "Course" ||
          "Qualification";

        if (stageCurrentTab) {
          return <DynamicStageDrawer currentTab={contentTab} />;
        }

        return (
          <div className="p-5 text-gray-500">
            Content not found for {contentTab}
          </div>
        );
    }
  };

  const handleTabSelect = (tabValue: any) => {
    const tab = tabs.find((t: any) => t.value === tabValue) as any;

    if (tab?.type === "stage") {
      if (!tab?.id) return;
      dispatch(
        setIdentifierAndIdentifier({
          stageId: tab.id,
          unique_identifier: tab?.identifier,
        })
      );
    }

    dispatch(setLeadCurrentTab(tabValue));
  };

  const sheetLead = formLead ?? lead;

  /** Pixel height of the sticky header (lead + stage + tabs) for secondary `position: sticky` (e.g. Notes composer). */
  const drawerStickyHeaderRef = useRef<HTMLDivElement>(null);
  const [drawerStickyTopPx, setDrawerStickyTopPx] = useState(0);

  useLayoutEffect(() => {
    if (currentTab === "Chat") return;
    const el = drawerStickyHeaderRef.current;
    if (!el) return;
    const measure = () => {
      setDrawerStickyTopPx(Math.round(el.getBoundingClientRect().height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentTab, isLoading, lead?.id, tabs.length]);

  const drawerStickyStyle: CSSProperties | undefined =
    currentTab === "Chat"
      ? undefined
      : ({
          ["--view-lead-drawer-sticky-h" as string]: `${drawerStickyTopPx}px`,
        } as CSSProperties);

  return (
    <>
      <AssignUserSheet />
      <PeerTransferSheet />
      <ViewAndEditRightSideSheet
        open={open}
        handleClose={onClose}
        width={VIEW_LEAD_WIDTH[breakpoint]}
        actions={
          isViewLocked || currentTab === "Chat" ? undefined : <Actions />
        }
        noScroll={currentTab === "Chat" || showAccessDeniedOnly}
      >
        {isLoading && !lead ? (
          <div className="flex h-full min-h-[50vh] items-center justify-center">
            <FullScreenLoader />
          </div>
        ) : showAccessDeniedOnly ? (
          <div className="flex flex-col h-full min-h-0 bg-white rounded-t-3xl">
            <div className="px-5 pt-4 pb-3 border-b border-[var(--color-black-1)] shrink-0">
              <LeadHeader onClose={onClose} />
            </div>
            <RevokedAccessExplainer leadId={lead?.id} />
          </div>
        ) : overrideComp ? (
          overrideComp
        ) : (
          <div
            className={`flex flex-col ${
              currentTab === "Chat" ? "h-full overflow-hidden" : ""
            } ${isViewLocked ? "bg-[#d5d9d7]" : ""}`}
            style={drawerStickyStyle}
          >
            <div
              ref={drawerStickyHeaderRef}
              className={`${
                currentTab === "Chat" ? "shrink-0" : "sticky top-0"
              } z-20 rounded-t-3xl ${
                isViewLocked ? "bg-[#d5d9d7]" : "bg-white"
              } ${isViewLocked ? "opacity-[0.58]" : ""}`}
            >
              <div className="px-5 pt-4 pb-3 flex flex-col gap-3 border-b border-[var(--color-black-1)]">
                <LeadHeader onClose={onClose} />
                <div className="relative">
                  <div
                    className={
                      isViewLocked
                        ? "pointer-events-none select-none flex flex-col gap-3 grayscale-[0.35]"
                        : "flex flex-col gap-3"
                    }
                  >
                    <div className="overflow-x-auto">
                      <StatusAndUsersSection />
                    </div>
                    <StageSection curStage={sheetLead?.stage} />
                    <LeadToolStrip />
                  </div>
                </div>
              </div>

              <div
                className={`border-[var(--color-black-1)] border-b pb-2 pt-1 relative ${
                  isViewLocked ? "opacity-[0.58]" : ""
                }`}
              >
                {/* Fixed strip height so the arrow never resizes the drawer header (ResizeObserver). */}
                <div className="flex h-10 min-h-10 max-h-10 min-w-0 items-center gap-1 pr-1">
                  <div
                    ref={attachTabStrip}
                    className={`flex h-full min-h-0 min-w-0 flex-1 items-stretch overflow-x-auto overflow-y-hidden ${
                      isViewLocked
                        ? "pointer-events-none select-none grayscale-[0.35]"
                        : ""
                    }`}
                  >
                    {/*
                      Indicator override: the default indicator sits at
                      `-bottom-[8px]` (outside the wrapper) but the scroll
                      strip above has `overflow-y-hidden`, which clipped the
                      gradient underline entirely. Pin it to `bottom-0` so it
                      lives inside the visible 40px tab strip — the inner
                      pill is 32px centered, leaving exactly 4px below for
                      the underline to land cleanly.
                    */}
                    <DraggableTabsWrapper
                      className="h-full min-h-0"
                      tabs={tabs}
                      activeTab={activeTabForBar}
                      setActiveTab={handleTabSelect}
                      onReorder={layoutLocked ? undefined : handleReorder}
                      reorderDisabled={layoutLocked}
                      indicatorClassName="bottom-0"
                    />
                  </div>
                  {!isViewLocked &&
                  tabs.length > 0 &&
                  (tabStripOverflow.right || tabStripOverflow.left) ? (
                    <button
                      type="button"
                      className={cn(
                        "lead-drawer-tab-arrow relative flex size-9 shrink-0 items-center justify-center self-center overflow-hidden rounded-full border border-[#e6e8e7] bg-white/95 text-[#1e302a] outline-none transition-transform duration-300 ease-out hover:scale-105 active:scale-[0.97]",
                        tabStripOverflow.right && "lead-drawer-tab-arrow--shine"
                      )}
                      aria-label={
                        tabStripOverflow.right
                          ? "Show more tabs"
                          : "Back to first tabs"
                      }
                      title={
                        tabStripOverflow.right ? "More tabs" : "Back to start"
                      }
                      onClick={slideTabStrip}
                    >
                      {tabStripOverflow.right ? (
                        <ChevronRight
                          className="lead-drawer-tab-arrow__icon size-[18px]"
                          strokeWidth={2.5}
                          aria-hidden
                        />
                      ) : (
                        <ChevronLeft
                          className="size-[18px]"
                          strokeWidth={2.5}
                          aria-hidden
                        />
                      )}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>

            <div
              className={`relative flex-1 min-h-0 flex flex-col ${
                currentTab === "Chat" ? "min-h-0 overflow-hidden" : ""
              } ${contentTab === "Attachment" ? "bg-[#f4f6f8]" : ""}`}
            >
              <div
                className={
                  isViewLocked
                    ? "pointer-events-none select-none flex-1 min-h-0 flex flex-col opacity-[0.42] grayscale-[0.55]"
                    : `flex-1 min-h-0 flex flex-col ${currentTab === "Chat" ? "overflow-hidden" : ""} ${contentTab === "Attachment" ? "bg-[#f4f6f8]" : ""}`
                }
              >
                {renderTabContent()}
              </div>
            </div>
          </div>
        )}
      </ViewAndEditRightSideSheet>
      <FollowUpOverrideConfirmation />
    </>
  );
}

/** Drawer tabs that only mean anything with deals (Settings → Manage → "Disable deals"). */
const DEAL_TABS = new Set(["Course", "Program", "Service", "Payments", "Fees"]);

export default function ViewLead({
  leadId,
  open,
  onClose,
  routeStageId,
  routeStatusId,
  routeSubStatusId,
  defaultChildId,
}: ViewLeadProps) {
  if (!leadId) return null;

  return (
    <LeadFormProvider
      leadId={leadId}
      routeStageId={routeStageId}
      routeStatusId={routeStatusId}
      routeSubStatusId={routeSubStatusId}
      defaultChildId={defaultChildId}
    >
      {/* Stage-requirement stars for whichever entity the drawer is on. */}
      <ActiveEntityRequiredFieldMarks>
        <ViewLeadModalShell leadId={leadId} onClose={onClose} open={open} />
      </ActiveEntityRequiredFieldMarks>
    </LeadFormProvider>
  );
}
